// prepareProductPageMeta.js
import { generateProductJsonLd } from '../resource/generateProductJsonLd.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { BRANDING_NAME } from '../RemoteConfigKeys.js';

/**
 * Prepare meta tags and JSON-LD for Product page
 * @param {Object} options
 * @param {Object} options.state - Redux state
 * @param {string} options.productId - Product ID
 * @param {string} options.productType - Product type
 * @param {string} options.imageUrl - Product image URL
 * @param {string} options.releaseDate - Release date (ISO 8601)
 * @param {Array} options.offers - Array of price information
 * @param {Array} options.interactionStatistic - Array of interaction statistics
 * @param {string} options.hostname - Request hostname
 * @param {boolean} options.includeJsonLd - Whether to include JSON-LD data
 * @param {string} options.canonical - Canonical URL (optional, if not provided, will be null)
 * @param {Array} options.alternates - Alternate language links (optional)
 * @param {Object} options.i18n - i18n instance (optional, will use getI18nInstance if not provided)
 * @param {string} options.title - Product title (optional, will be translated if not provided)
 * @param {string} options.description - Product description (optional, will be generated if not provided)
 * @param {number} options.price - Product price (optional, for multilingual description)
 * @param {string} options.currency - Currency code (optional, for multilingual description)
 * @param {number} options.decimalFactor - Decimal factor (optional, for multilingual description)
 * @param {Array} options.secondaryBadge - Secondary badge data (optional, for multilingual description)
 * @param {Array} options.availableLanguages - Available languages (optional, for multilingual JSON-LD)
 * @returns {Array} Array of meta tags (for RouteMetaTags)
 */
export const prepareProductPageMeta = async ({
  state,
  productId,
  productType,
  imageUrl,
  releaseDate,
  offers = [],
  interactionStatistic = [],
  hostname = 'swag.live',
  includeJsonLd = false,
  canonical = null,
  alternates = null,
  i18n: i18nParam,
  title: titleParam,
  description: descriptionParam,
  price,
  currency,
  decimalFactor,
  secondaryBadge = [],
  availableLanguages = [],
}) => {
  // Get i18n instance - use provided instance or fallback to global
  const { i18n } = i18nParam ? { i18n: i18nParam } : getI18nInstance();

  // Get translated title
  const title =
    titleParam ||
    i18n.t(getTranslationKey({ key: `shop_product_name_${productId}` }), {
      ns: TranslationNamespace.PRODUCT,
    });

  // Get description (if provided, use it; otherwise it will be generated by caller)
  const description = descriptionParam || '';

  // Get branding name from remote config
  const brandingName = getRemoteConfigData(state, BRANDING_NAME) || 'SWAG';

  const fullTitle = `${title} | ${brandingName}`;
  const origin = `https://${hostname}`;
  const url = canonical || `${origin}/product/${productId}`;

  // Prepare meta options
  const metaOptions = {
    title: fullTitle,
    description,
    url,
    images: imageUrl
      ? [
          {
            url: imageUrl,
            width: 320,
            height: 320,
          },
        ]
      : [],
    ogType: 'product',
    twitterCard: 'summary',
    siteName: brandingName,
    hostname,
    alternates: {
      canonical,
      alternates,
    },
  };

  // Add JSON-LD if requested
  if (includeJsonLd) {
    const jsonLd = await generateProductJsonLd({
      productId,
      productType,
      title,
      description,
      releaseDate,
      offers,
      interactionStatistic,
      origin,
      i18n,
      price,
      currency,
      decimalFactor,
      secondaryBadge,
      availableLanguages,
    });
    metaOptions.jsonLd = jsonLd;
  }

  return getMetaTagsForHelmet(metaOptions);
};
